import { InjectionKey, Ref } from 'vue'
import { emitter } from '@/plugins/emitter'
import JSEncrypt from 'jsencrypt'

export type RtSendReturn = {
  linkRealtimeTransferCommonConsult: () => Promise<void>
  sendRealtimeTransferCommonConsult: () => Promise<void>
  sendRecurrentConsult: () => Promise<void>
  sendXMileConsult: () => Promise<void>
  sendTrytApiConsult: () => Promise<void>
  sendWorkportConsult: () => Promise<void>
  sendHrcareerConsult: () => Promise<void>
  sendRecreeConsult: () => Promise<void>
  sendCareerdraftConsult: () => Promise<void>
  sendRozasConsult: () => Promise<void>
  sendNeocareerConsult: () => Promise<void>
  sendRecruitMcConsult: () => Promise<void>
  sendAdtopConsult: () => Promise<void>
  sendAgrinaviConsult: () => Promise<void>
  sendAsiroConsult: () => Promise<void>
  sendBatonConsult: () => Promise<void>
  sendBridgeConsult: () => Promise<void>
  sendCaplaConsult: () => Promise<void>
  sendDelyConsult: () => Promise<void>
  sendGrowtrailConsult: () => Promise<void>
  sendHitotechConsult: () => Promise<void>
  sendHitotechneocareerConsult: () => Promise<void>
  sendJaicConsult: () => Promise<void>
  sendJobs919Consult: () => Promise<void>
  sendKiraraConsult: () => Promise<void>
  sendKrasysConsult: () => Promise<void>
  sendMiraiyuConsult: () => Promise<void>
  sendMynaviBaiteConsult: () => Promise<void>
  sendMynaviMsConsult: () => Promise<void>
  sendPeacekaigoConsult: () => Promise<void>
  sendPersolinnovationConsult: () => Promise<void>
  sendPleiadesConsult: () => Promise<void>
  sendReastageConsult: () => Promise<void>
  sendSimpleConsult: () => Promise<void>
  sendSystemsharedConsult: () => Promise<void>
  sendTechouseConsult: () => Promise<void>
  sendTunagubaConsult: () => Promise<void>
  sendUsenworkingConsult: () => Promise<void>
  sendYagishConsult: () => Promise<void>
  sendNewRtSystemConsult: () => Promise<void>
  sendYagishCareerConsult: () => Promise<void>
  sendRealiveConsult: () => Promise<void>
  sendRealtimeTransferAppealConsult: () => Promise<void>
  /**
   * @deprecated `sendRealtimeTransferAppealConsult` の旧名エイリアス。
   * MedleyLinkDialog 等の既存利用箇所のために残してある。新規利用禁止。
   */
  sendMedleyConsult: () => Promise<void>
  sendBizreachConsult: () => Promise<void>
}

export interface RtSendContext {
  vm: any
  isAgree: Ref<boolean>
  isRealtimeTransferCommonSended: Ref<boolean>
  fullscreenLoading: Ref<boolean>
  pdfFormData: Ref<FormData>
  formatid: Ref<string>
  username: Ref<string>
  clientId: Ref<string>
  dialogName: Ref<string>
  permissionType: Ref<number>
  basicData: Ref<Record<string, any>>
  dialogInfo: Ref<Record<string, any>>
  isMypage: Ref<boolean>
  createFilename: (ext: string) => string
  getPrefName: () => string
  getCityName: () => string
  bizreachPassword?: Ref<string>
  bizreachPasswordFormRef?: Ref<any>
}

export type RtSendFactory = (ctx: RtSendContext) => RtSendReturn

export const RtSendFactoryKey: InjectionKey<RtSendFactory> = Symbol('useRealtimeTransferSend')

function resolveEndpoint (vm: any, endpointKey?: string): string {
  if (endpointKey && vm.$varuna.endPoints[endpointKey]) {
    return vm.$varuna.endPoints[endpointKey]
  }
  return vm.$varuna.endPoints.sendRealtimeTransferCommon
}

async function reloadAndGetToken (vm: any): Promise<string | null> {
  await vm.$firebase.reloadToken()
  const token = vm.$firebase.accessToken
  if (!token || !vm.$firebase.user.email) {
    vm.$message({ message: 'ログインが必要です', type: 'warning' })
    return null
  }
  return token
}

function checkPreConditions (vm: any, isAgree: Ref<boolean>, isSended: Ref<boolean>): boolean {
  if (!isAgree.value) {
    vm.$message({ message: 'チェックしてから同意ボタンを押してください。', type: 'error' })
    return false
  }
  if (isSended.value) return false
  if (!vm.$deviceLib.isOnline) {
    vm.$message({ message: '現在オフラインです。インターネットにつながってから再度お試しください', type: 'warning' })
    return false
  }
  return true
}

export function useRealtimeTransferSend (ctx: RtSendContext) {
  const {
    vm, isAgree, isRealtimeTransferCommonSended, fullscreenLoading,
    pdfFormData, formatid, username, clientId, dialogName,
    permissionType, basicData, dialogInfo, isMypage,
    createFilename, getPrefName, getCityName
  } = ctx

  async function linkRealtimeTransferCommonConsult () {
    const redirectUrl = dialogInfo.value.link
    const pendingTab = redirectUrl ? window.open('', '_blank') : null
    const token = await reloadAndGetToken(vm)
    if (!token) {
      if (pendingTab && !pendingTab.closed) pendingTab.close()
      return
    }
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('client_id', clientId.value)
    formData.append('dialog_name', dialogName.value)
    formData.append('permission_type', String(permissionType.value))
    formData.append('storage_data', JSON.stringify(basicData.value))
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '3')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    formData.append('ptype', 'onlylink')
    const endpoint = resolveEndpoint(vm, dialogInfo.value.sendApiEndpointKey)
    vm.$axios
      .post(endpoint, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
        if (clientId.value === 'roxx_api') {
          emitter.emit('suppress_client_all_permissions', clientId.value)
        }
        if (redirectUrl) {
          if (pendingTab && !pendingTab.closed) {
            pendingTab.location.href = redirectUrl
          } else {
            window.open(redirectUrl, '_blank')
          }
        }
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
        if (pendingTab && !pendingTab.closed) pendingTab.close()
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendRealtimeTransferCommonConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    if (dialogInfo.value.link) { linkRealtimeTransferCommonConsult(); return }
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('client_id', clientId.value)

    const prefName = getPrefName()
    const cityName = getCityName()
    const addr1 = basicData.value.address1 || ''
    const addr2 = basicData.value.address2 || ''
    const otherName = addr1.substring(
      addr1.indexOf(cityName) + cityName.length
    ) + addr2

    formData.append('pref_name', prefName)
    formData.append('city_name', cityName)
    formData.append('town_name', otherName)
    formData.append('dialog_name', dialogName.value)
    formData.append('permission_type', String(permissionType.value))
    formData.append('storage_data', JSON.stringify(basicData.value))
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '3')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    if (dialogInfo.value.gradeYear) {
      formData.append('graduation_year', dialogInfo.value.gradeYear + '年卒')
    }
    if (dialogInfo.value.inputEmail) {
      const userEmail: string = vm.$firebase.user.email
      if (!userEmail || userEmail.indexOf('@privaterelay.appleid.com') > 0) {
        formData.append('inputemail', dialogInfo.value.inputEmail)
      }
    }
    if (dialogInfo.value.useDataSource) {
      formData.append('data_source', formatid.value.includes('shokumukeirekisho') ? 'work_history' : 'resume')
    }
    const endpoint = resolveEndpoint(vm, dialogInfo.value.sendApiEndpointKey)
    vm.$axios
      .post(endpoint, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
        if (clientId.value === 'roxx_api') {
          emitter.emit('suppress_client_all_permissions', clientId.value)
        }
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => {
        fullscreenLoading.value = false
        if (dialogInfo.value.setTimeLink) {
          setTimeout(() => { window.open(dialogInfo.value.setTimeLink, '_blank') }, 1000)
        }
      })
  }

  async function sendRecurrentConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    if (dialogInfo.value.link) { linkRealtimeTransferCommonConsult(); return }
    const token = await reloadAndGetToken(vm)
    if (!token) return
    const ipinfo = await vm.$axios.get('https://ipinfo.io/json', { timeout: 60000 })
    const useragent = navigator.userAgent
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('client_id', clientId.value)
    formData.append('recurrent_words', vm.$globalVar.RecurrentWords)
    formData.append('ip', ipinfo.data.ip)
    formData.append('user_agent', useragent)

    const prefName = getPrefName()
    const cityName = getCityName()
    const addr1 = basicData.value.address1 || ''
    const addr2 = basicData.value.address2 || ''
    const otherName = addr1.substring(
      addr1.indexOf(cityName) + cityName.length
    ) + addr2

    formData.append('pref_name', prefName)
    formData.append('city_name', cityName)
    formData.append('town_name', otherName)
    formData.append('dialog_name', dialogName.value)
    formData.append('permission_type', String(permissionType.value))

    let skillList: string[] = []
    if (clientId.value === 'recurrent_api') {
      const raw = vm.$globalVar.RecurrentWords
      skillList = Array.isArray(raw) ? raw.slice() : (raw ? ([] as string[]).concat(raw) : [])
      skillList = skillList.filter((s: any) => s != null && String(s).trim() !== '')
      if (skillList.length === 0) skillList = ['（免許・資格なし）']
    }
    const storageDataToSend = Object.assign({}, basicData.value, { skill_list: skillList })
    formData.append('storage_data', JSON.stringify(storageDataToSend))
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '3')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    formData.append('site_origin', process.env.VUE_APP_SITE_URL ?? window.location.origin)
    vm.$axios
      .post(vm.$varuna.endPoints.sendRecurrent, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
        if (clientId.value === 'roxx_api') {
          emitter.emit('suppress_client_all_permissions', clientId.value)
        }
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => {
        fullscreenLoading.value = false
        if (dialogInfo.value.setTimeLink) {
          setTimeout(() => { window.open(dialogInfo.value.setTimeLink, '_blank') }, 1000)
        }
      })
  }

  async function sendXMileConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('dialog_name', 'xmile_dialog')
    formData.append('permission_type', String(permissionType.value))
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '3')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.sendXMile, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendTrytApiConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true

    const prefName = getPrefName()
    const cityName = getCityName()
    const addr1 = basicData.value.address1 || ''
    const addr2 = basicData.value.address2 || ''
    const otherName = addr1.substring(
      addr1.indexOf(cityName) + cityName.length
    ) + addr2

    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('yaginame', username.value)
    formData.append('pref_name', prefName)
    formData.append('city_name', cityName)
    formData.append('town_name', otherName)
    formData.append('qualifications', dialogInfo.value.qualifications ?? '正看護師')
    formData.append('employments', dialogInfo.value.employments ?? '常勤(日勤のみ)')
    formData.append('workstate', dialogInfo.value.workstate ?? '就業中')
    formData.append('request_date', dialogInfo.value.request_date ?? '未定')
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('dialog_name', 'tryt_dialog')
    formData.append('permission_type', String(permissionType.value))
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '6')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.sendTrytApi, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
      })
      .catch((error: any) => {
        vm.$message({ message: `応募失敗しました。${error?.response?.data?.message ?? ''}`, type: 'error' })
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendWorkportConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('storage_data', JSON.stringify(basicData.value))
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('dialog_name', 'workport-dialog')
    formData.append('permission_type', String(permissionType.value))
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '3')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.sendWorkport, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendHrcareerConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('dialog_name', dialogName.value)
    formData.append('permission_type', String(permissionType.value))
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '3')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.sendHrcareer, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendRecreeConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('dialog_name', dialogName.value)
    formData.append('storage_data', JSON.stringify(basicData.value))
    formData.append('permission_type', String(permissionType.value))
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '3')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.sendRecree, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendCareerdraftConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('dialog_name', dialogName.value)
    formData.append('permission_type', String(permissionType.value))
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '3')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.sendCareerdraft, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendRozasConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('dialog_name', dialogName.value)
    formData.append('permission_type', String(permissionType.value))
    formData.append('storage_data', JSON.stringify(basicData.value))
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '3')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.sendRozas, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendNeocareerConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('dialog_name', dialogName.value)
    formData.append('permission_type', String(permissionType.value))
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '3')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.sendNeocareer, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendRecruitMcConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('dialog_name', dialogName.value)
    formData.append('permission_type', String(permissionType.value))
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '3')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.sendRecruitMc, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendAdtopConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('dialog_name', dialogName.value)
    formData.append('permission_type', String(permissionType.value))
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '2')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.adtopPdfMail, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendAgrinaviConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('dialog_name', dialogName.value)
    formData.append('permission_type', String(permissionType.value))
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '3')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.sendAgrinavi, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendAsiroConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('dialog_name', dialogName.value)
    formData.append('permission_type', String(permissionType.value))
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '3')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.sendAsiro, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendBatonConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('dialog_name', dialogName.value)
    formData.append('permission_type', String(permissionType.value))
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '3')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.sendBaton, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendBridgeConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('dialog_name', dialogName.value)
    formData.append('permission_type', String(permissionType.value))
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '3')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.sendBridge, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendCaplaConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('dialog_name', dialogName.value)
    formData.append('permission_type', String(permissionType.value))
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '3')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.sendCapla, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
        vm.$message({ duration: 0, showClose: true, message: '担当者からあなたのメールアドレスに連絡が届くことがあります！', type: 'info' })
      })
      .catch((error: any) => {
        vm.$message({ duration: 0, showClose: true, message: '応募に失敗しました。入力内容を確認して再度ご応募ください', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendDelyConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('dialog_name', dialogName.value)
    formData.append('permission_type', String(permissionType.value))
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '3')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.sendDely, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendGrowtrailConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('dialog_name', dialogName.value)
    formData.append('permission_type', String(permissionType.value))
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '3')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.sendGrowtrail, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendHitotechConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('dialog_name', dialogName.value)
    formData.append('permission_type', String(permissionType.value))
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '3')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.sendHitotech, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendHitotechneocareerConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true
    const createRequestFormData = () => {
      const requestFormData = new FormData()
      pdfFormData.value.forEach((value, key) => {
        requestFormData.append(key, value)
      })
      return requestFormData
    }
    const formData = createRequestFormData()
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('dialog_name', dialogName.value)
    formData.append('permission_type', '3')
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '3')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.sendHitotech, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', 'hitotech_admin', 3)
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
    const formData2 = createRequestFormData()
    formData2.append('token', token)
    formData2.append('apikey', vm.$firebase.config.apiKey)
    formData2.append('uuid', vm.$firebase.user.uid)
    formData2.append('format_id', formatid.value)
    formData2.append('yaginame', username.value)
    formData2.append('app_flag', vm.$site.appFlag)
    formData2.append('dialog_name', dialogName.value)
    formData2.append('permission_type', '1')
    formData2.append('filename', createFilename('pdf'))
    formData2.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData2.append('version', '3')
    formData2.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.sendNeocareer, formData2, { timeout: 60000 })
      .then(() => {
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', 'neocareer_admin', 1)
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendJaicConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('dialog_name', dialogName.value)
    formData.append('permission_type', String(permissionType.value))
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '2')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.sendJaic, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendJobs919Consult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('dialog_name', dialogName.value)
    formData.append('storage_data', JSON.stringify(basicData.value))
    formData.append('permission_type', String(permissionType.value))
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '3')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.sendJobs919, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendKiraraConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('dialog_name', dialogName.value)
    formData.append('permission_type', String(permissionType.value))
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '1')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.sendKirara, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendKrasysConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('dialog_name', dialogName.value)
    formData.append('permission_type', String(permissionType.value))
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '3')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.sendKrasys, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendMiraiyuConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('dialog_name', dialogName.value)
    formData.append('permission_type', String(permissionType.value))
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '2')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.sendMiraiyu, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendMynaviBaiteConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('dialog_name', dialogName.value)
    formData.append('permission_type', String(permissionType.value))
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '3')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.sendMynaviBaite, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendMynaviMsConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('dialog_name', dialogName.value)
    formData.append('permission_type', String(permissionType.value))
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '2')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.sendMynaviMs, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendPeacekaigoConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('dialog_name', dialogName.value)
    formData.append('storage_data', JSON.stringify(basicData.value))
    formData.append('permission_type', String(permissionType.value))
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '3')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.sendPeacekaigo, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendPersolinnovationConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('dialog_name', dialogName.value)
    formData.append('permission_type', String(permissionType.value))
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '3')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.sendPersolinnovation, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendPleiadesConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('dialog_name', dialogName.value)
    formData.append('permission_type', String(permissionType.value))
    formData.append('storage_data', JSON.stringify(basicData.value))
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '3')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.sendPleiades, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendReastageConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('dialog_name', dialogName.value)
    formData.append('permission_type', String(permissionType.value))
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '3')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.sendReastage, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendSimpleConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('dialog_name', dialogName.value)
    formData.append('permission_type', String(permissionType.value))
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '1')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.sendSimple, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendSystemsharedConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('dialog_name', dialogName.value)
    formData.append('permission_type', String(permissionType.value))
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '2')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.sendSystemShared, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendTechouseConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('dialog_name', dialogName.value)
    formData.append('permission_type', String(permissionType.value))
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '3')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.sendTechouse, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendTunagubaConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('dialog_name', dialogName.value)
    formData.append('permission_type', String(permissionType.value))
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '3')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.sendTunaguba, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendUsenworkingConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('dialog_name', dialogName.value)
    formData.append('permission_type', String(permissionType.value))
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '3')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.sendUsenworking, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendYagishConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('yaginame', '応募者')
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('client_id', clientId.value)
    formData.append('dialog_name', dialogName.value)
    formData.append('permission_type', String(permissionType.value))
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '2')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.yagishRecruitPdfMail, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
        vm.$eventHub.$emit('set_is_sendedYagishIntern', true)
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendNewRtSystemConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true

    const prefName = getPrefName()
    const cityName = getCityName()
    const addr1 = basicData.value.address1 || ''
    const addr2 = basicData.value.address2 || ''
    const otherName = addr1.substring(
      addr1.indexOf(cityName) + cityName.length
    ) + addr2

    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('condition_id', dialogInfo.value.conditionId ?? '')
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('client_id', clientId.value)
    formData.append('pref_name', prefName)
    formData.append('city_name', cityName)
    formData.append('town_name', otherName)
    formData.append('dialog_name', dialogName.value)
    formData.append('permission_type', String(permissionType.value))
    formData.append('storage_data', JSON.stringify(basicData.value))
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '3')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.sendNewRtSystem, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendYagishCareerConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('dialog_name', dialogName.value)
    formData.append('permission_type', String(permissionType.value))
    formData.append('filename', createFilename('pdf'))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '2')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.sendYagishCareer, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  async function sendRealiveConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    const token = await reloadAndGetToken(vm)
    if (!token) return
    fullscreenLoading.value = true
    const formData = pdfFormData.value
    formData.append('token', token)
    formData.append('apikey', vm.$firebase.config.apiKey)
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('yaginame', username.value)
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('filename', createFilename('pdf'))
    formData.append('dialog_name', dialogName.value)
    formData.append('permission_type', String(permissionType.value))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '2')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('reqflg', '2')
    vm.$axios
      .post(vm.$varuna.endPoints.sendRealive, formData, { timeout: 60000 })
      .then(() => {
        isRealtimeTransferCommonSended.value = true
        vm.$globalVar.eventTag()
        vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
      })
      .catch((error: any) => {
        vm.$message({ message: '送信失敗しました', type: 'error' })
        vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
      })
      .finally(() => { fullscreenLoading.value = false })
  }

  /**
   * Appeal ダイアログ専用: 送信前タブ open のみ担当し、POST は Common に委譲する。
   *
   * タブ open 優先順位:
   *  (a) `tabLinkUrl` / `tabLinkUrlSp` あり → 汎用 URL
   *  (b) それ以外 → 変更なし
   *
   * 送信・エンドポイント解決・FormData はすべて `sendRealtimeTransferCommonConsult` に任せる。
   */
  function openAppealTabLinksIfNeeded (): void {
    const info = dialogInfo.value
    const isMobile = vm.$deviceLib.isMobileDevice === 'SP'

    if (info.tabLinkUrl || info.tabLinkUrlSp) {
      const directUrl = isMobile ? (info.tabLinkUrlSp ?? info.tabLinkUrl) : info.tabLinkUrl
      if (directUrl) {
        info.link = directUrl
      }
    }
  }

  async function sendRealtimeTransferAppealConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return
    openAppealTabLinksIfNeeded()
    await sendRealtimeTransferCommonConsult()
  }

  /**
   * @deprecated `sendRealtimeTransferAppealConsult` の旧名エイリアス。
   * MedleyLinkDialog 等の既存利用箇所のために残してある。新規利用禁止。
   */
  const sendMedleyConsult = sendRealtimeTransferAppealConsult

  async function sendBizreachConsult () {
    if (!checkPreConditions(vm, isAgree, isRealtimeTransferCommonSended)) return

    const formRef = ctx.bizreachPasswordFormRef
    if (formRef?.value) {
      const valid = await new Promise<boolean>((resolve) => {
        formRef.value.validate((v: boolean) => resolve(v))
      })
      if (!valid) return
    }

    const token = await reloadAndGetToken(vm)
    if (!token) return

    const isMobile = vm.$deviceLib.isMobileDevice === 'SP'
    fullscreenLoading.value = true
    try {
      const formData = pdfFormData.value
      formData.append('uuid', vm.$firebase.user.uid)
      formData.append('device_flg', isMobile ? 'SP' : 'PC')
      formData.append('app_flag', vm.$site.appFlag)
      formData.append('dialog_name', dialogName.value)
      formData.append('permission_type', String(permissionType.value))
      formData.append('client_id', clientId.value)
      formData.append('format_id', formatid.value)

      formData.append('lastname', basicData.value.lastName)
      formData.append('firstname', basicData.value.firstName)
      formData.append('email', basicData.value.email)
      formData.append('birthYear', basicData.value.birthYear)
      formData.append('birthMonth', basicData.value.birthMonth)
      formData.append('birthDay', basicData.value.birthDay)
      formData.append('sex', basicData.value.sex)

      const password = ctx.bizreachPassword?.value ?? ''
      const encryptor = new JSEncrypt()
      const publicKey = (process.env.VUE_APP_BIZREACH_PASS_ENCODING_CERT as string).replace(/\\n/g, '\n')
      encryptor.setPublicKey(publicKey)
      const encrypted = encryptor.encrypt(password)
      if (!encrypted) throw new Error('暗号化に失敗しました。鍵のフォーマットを確認してください。')
      formData.append('BizreachPassword', encrypted)

      formData.append('postnumber', basicData.value.postNumber)
      formData.append('address1', basicData.value.address1)
      let tel = String(basicData.value.tel1 || '') + String(basicData.value.tel2 || '') + String(basicData.value.tel3 || '')
      tel = tel.replace(/[^0-9０-９]/g, '')
      formData.append('tel', tel)

      const getItem = (key: string): Promise<any> => new Promise((resolve) => {
        if (!vm.$getItem) { resolve(null); return }
        vm.$getItem(key, (err: any, data: any) => resolve(err ? null : data))
      })
      const storedRirekisho = await getItem('format_rirekisho')
      const storedShokumukeirekisho = await getItem('format_shokumukeirekisho')

      const gyoushuCd = basicData.value.gyoushuCd
      const shokushuCd = basicData.value.shokushuCd
      const yakushokuBusho = basicData.value.yakushokuBusho
      if (!gyoushuCd) throw new Error('業種コード（gyoushuCd）が未設定です')
      if (!shokushuCd) throw new Error('職種コード（shokushuCd）が未設定です')
      if (!yakushokuBusho) throw new Error('役職部署（yakushokuBusho）が未設定です')
      formData.append('last_job_histroty_gyoushuCd', gyoushuCd)
      formData.append('last_job_histroty_shokushuCd', shokushuCd)
      formData.append('last_job_histroty_yakushokuBusho', yakushokuBusho)

      if (!storedRirekisho) throw new Error('履歴書がありません')

      let jobHistories = JSON.parse(JSON.stringify(storedRirekisho.jobHistories))
      jobHistories = jobHistories.filter((jh: any) => jh.year !== '' || jh.month !== '' || jh.description !== '現在に至る')
      let lastJob = jobHistories.pop()
      let presentFlg = true
      let endYear = ''
      let endMonth = ''

      if (!lastJob) throw new Error('履歴書の職歴がありません')
      const rirekiDesc = String(lastJob.description || '')
      if (rirekiDesc.includes('退社') || rirekiDesc.includes('退職')) {
        presentFlg = false
        endYear = lastJob.year
        endMonth = lastJob.month
        if (!endYear && !endMonth) throw new Error('退職年月が未設定です。退職年月を入力してください。')
        lastJob = jobHistories.pop()
        if (!lastJob) throw new Error('退職に対応する入社側の職歴が見つかりません')
        formData.append('last_job_histroty_company_endYear', endYear)
        formData.append('last_job_histroty_company_endMonth', endMonth)
      }
      if (lastJob) {
        // 履歴書の職歴 description から会社名と業務内容を分離: 最初の空白(半角/全角/改行)で分割する。
        // 会社形態に関わらず「〇〇銀行 営業部」「〇〇証券 業務」「合同会社X 業務」等もカバー(日本のユーザーの主訴を解決)。
        // ただし業務内容が「入社/退社/退職/現在に至る」等の定型語のみの場合はフォールバック(両方 description)。
        // ★既知の制約: 会社名自体に空白を含む英語社名(例 "Amazon Web Services Japan ソリューションアーキテクト")は会社名が最初の単語のみに切れる。日本のユーザーがほぼいないため許容、将来AI判定で改善余地あり。
        // マッチしない(空白なし)場合や定型語のみの場合は両方 description にフォールバック(現状維持・後方互換)
        const _desc = String(lastJob.description || '')
        const _m = _desc.match(/^(\S+)\s+([\s\S]+)$/)
        const _standardKeywords = ['入社', '退社', '退職', '現在に至る']
        const _canSplit = _m && !_standardKeywords.includes(_m[2].trim())
        // dry-run 環境では会社名分離の詳細をコンソールに出力(原因調査用)
        if ((window as any).__BIZREACH_DRY_RUN__) {
          console.log('[Bizreach] 会社名/業務内容分離 デバッグ:', {
            original_description: _desc,
            description_charCodes: Array.from(_desc).slice(0, 30).map((c: any) => c + '(' + c.codePointAt(0) + ')').join(' '),
            regex_matched: !!_m,
            company_candidate: _m ? _m[1] : null,
            summary_candidate: _m ? _m[2] : null,
            is_standard_keyword: _m ? _standardKeywords.includes(_m[2].trim()) : null,
            can_split: _canSplit,
            final_company: _canSplit ? _m[1] : _desc,
            final_summary: _canSplit ? _m[2] : _desc
          })
        }
        formData.append('last_job_histroty_companyName', _canSplit ? _m[1] : _desc)
        formData.append('last_job_histroty_company_startYear', lastJob.year)
        formData.append('last_job_histroty_company_startMonth', lastJob.month)
        formData.append('last_job_resumeSummary', _canSplit ? _m[2] : _desc)
      }

      if (storedShokumukeirekisho) {
        const jhs = JSON.parse(JSON.stringify(storedShokumukeirekisho.jobHistories))
        const lastJH = jhs.filter((jh: any) => jh.companyName !== '' || jh.year !== '' || jh.month !== '' || jh.description !== '現在に至る').pop()
        if (lastJH) {
          formData.append('last_job_histroty_companyName', lastJH.companyName)
          formData.append('last_job_histroty_company_startYear', lastJH.startYear)
          formData.append('last_job_histroty_company_startMonth', lastJH.startMonth)
          formData.append('last_job_resumeSummary', storedShokumukeirekisho.jobSummary)
        }
        if (lastJH && lastJH.endYear && lastJH.endMonth) {
          presentFlg = false
          endYear = lastJH.endYear
          endMonth = lastJH.endMonth
          formData.append('last_job_histroty_company_endYear', endYear)
          formData.append('last_job_histroty_company_endMonth', endMonth)
        }
      }

      if (!presentFlg && (!endYear || !endMonth)) {
        throw new Error('職歴・履歴書データ整合性エラー。退職年月が未設定です。 endYear: ' + String(endYear) + ', endMonth: ' + String(endMonth))
      }
      formData.append('last_job_presentFlg', presentFlg ? 'true' : 'false')

      const educationalHistories = JSON.parse(JSON.stringify(storedRirekisho.educationalHistories))
      const lastEd = educationalHistories.filter((eh: any) => (eh.year !== '' || eh.month !== '') && eh.description && !eh.description.includes('入学')).pop()
      if (!lastEd) throw new Error('学歴がありません')
      formData.append('last_educational_history_description', lastEd.description + ' (' + lastEd.year + '年' + lastEd.month + '月)')

      formData.append('nownensyu', storedRirekisho.nowNensyu)
      formData.append('kibounensyu', storedRirekisho.kibouNensyu)
      formData.append('kibou_shokushuCd', basicData.value.kibouShokushuCd)
      formData.append('kibou_gyoushuCd', basicData.value.kibouGyoushuCd)

      // dry-run: ブラウザコンソールで `window.__BIZREACH_DRY_RUN__ = true` をセットすると送信スキップ + 内容ログ出力。
      // preview/staging 専用の確認用機能。本番(hostname に varuna-staging を含まない)では無視されるので、誤ったデバッグスニペット混入で本番送信がスキップされる事故を防ぐ
      const _isStagingEnv = typeof window !== 'undefined' && window.location.hostname.includes('varuna-staging')
      if (_isStagingEnv && (window as any).__BIZREACH_DRY_RUN__) {
        console.log('[Bizreach DRY RUN] 送信スキップ。送信内容:')
        for (const [key, value] of (formData as any).entries()) {
          console.log(`  ${key}: ${value}`)
        }
        vm.$message({ message: '[DRY RUN] 送信スキップ、内容はコンソール参照', type: 'info' })
        return
      }
      await vm.$axios.post(vm.$varuna.endPoints.sendBizreach, formData, { timeout: 60000 })
      isRealtimeTransferCommonSended.value = true
      vm.$globalVar.eventTag()
      vm.$eventHub.$emit('update_rt_condition', clientId.value, permissionType.value)
      if (dialogInfo.value.setTimeLink) {
        setTimeout(() => { window.open(dialogInfo.value.setTimeLink, '_blank') }, 1000)
      }
    } catch (error: any) {
      vm.$message({ message: error?.message ?? '送信失敗しました', type: 'error' })
      vm.$gtm.sendError('pdf mail', `send pdf mail failed ${error}`)
    } finally {
      fullscreenLoading.value = false
    }
  }

  return { linkRealtimeTransferCommonConsult, sendRealtimeTransferCommonConsult, sendRecurrentConsult, sendXMileConsult, sendTrytApiConsult, sendWorkportConsult, sendHrcareerConsult, sendRecreeConsult, sendCareerdraftConsult, sendRozasConsult, sendNeocareerConsult, sendRecruitMcConsult, sendAdtopConsult, sendAgrinaviConsult, sendAsiroConsult, sendBatonConsult, sendBridgeConsult, sendCaplaConsult, sendDelyConsult, sendGrowtrailConsult, sendHitotechConsult, sendHitotechneocareerConsult, sendJaicConsult, sendJobs919Consult, sendKiraraConsult, sendKrasysConsult, sendMiraiyuConsult, sendMynaviBaiteConsult, sendMynaviMsConsult, sendPeacekaigoConsult, sendPersolinnovationConsult, sendPleiadesConsult, sendReastageConsult, sendSimpleConsult, sendSystemsharedConsult, sendTechouseConsult, sendTunagubaConsult, sendUsenworkingConsult, sendYagishConsult, sendNewRtSystemConsult, sendYagishCareerConsult, sendRealiveConsult, sendRealtimeTransferAppealConsult, sendMedleyConsult, sendBizreachConsult }
}
